import { Factory, HardHat, Radio, Wrench } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { IconTile } from "@/components/ui/IconTile";
import { Reveal } from "@/components/marketing/Reveal";
import { CtaBand, PageHero, Section, SectionIntro } from "@/components/marketing/Section";

/**
 * Who it is for.
 *
 * Four trades rather than a list of twelve verticals, because the honest answer
 * is that TaskIQ suits one shape of work - a small crew, at an address they do
 * not own, with a ladder or a roof involved - and dressing that up as
 * "enterprise EHS for every industry" would fail the first discovery call.
 *
 * Each entry names the hazards that trade actually argues about. A visitor
 * decides in about four seconds whether a safety product understands their job,
 * and generic copy loses that decision every time.
 */

const TRADES = [
  {
    id: "utilities",
    icon: Factory,
    title: "Utilities",
    lead: "Overhead lines, poles, service drops and pad-mounted gear, at an address the crew has never seen before.",
    hazards: [
      "Service drop directly above the ladder position",
      "Pole rot and unstable pole-top work",
      "Ground disturbance near buried plant",
      "Dogs, and the fence that was not there last time",
    ],
    note: "The work is at a stranger's property, so nothing can be pre-installed and nothing can be assumed about coverage.",
  },
  {
    id: "telecom",
    icon: Radio,
    title: "Telecom & fibre",
    lead: "Aerial builds, splice work, pedestal and rooftop nodes - often shared with a power utility on the same pole.",
    hazards: [
      "Clearance from energised conductors on shared poles",
      "Rooftop edge and unguarded skylights",
      "Traffic exposure on a shoulder splice",
      "Confined pedestals and vaults",
    ],
    note: "Crews are subcontracted and turnover is high, so the tool has to be usable on the first shift, not the fifth.",
  },
  {
    id: "facilities",
    icon: Wrench,
    title: "Facilities & HVAC",
    lead: "Rooftop units, plant rooms, ceiling voids and the equipment cupboard that has become a store room.",
    hazards: [
      "Roof access and fall exposure at the unit",
      "Refrigerant and confined-space entry",
      "Ceiling void work above an occupied floor",
      "Blocked egress in a plant room",
    ],
    note: "The building's own safety file is often out of date, and the technician is the first person to look at the space in months.",
  },
  {
    id: "contractors",
    icon: HardHat,
    title: "Contractors",
    lead: "Crews working across many clients' sites, each with its own paperwork, each wanting evidence in its own format.",
    hazards: [
      "A different site induction at every address",
      "Evidence requested months after the job",
      "Subcontractors outside your own systems",
      "Multi-client reporting from one crew",
    ],
    note: "TaskIQ is standalone by default and integrates on request, so a client's compliance system is a connector rather than a migration.",
  },
];

export function Solutions() {
  return (
    <>
      <PageHero
        eyebrow="Solutions"
        title="Built for the eleventh address of the day."
        lead="TaskIQ suits one shape of work: a small crew, at a site nobody owns, with a ladder or a roof involved. Here is what that looks like in four trades."
      />

      <Section>
        <ul className="trade-list">
          {TRADES.map((trade) => (
            <Reveal as="li" key={trade.id}>
              <div id={trade.id} className="trade">
                <div>
                  <IconTile size="xl">
                    <trade.icon />
                  </IconTile>
                  <h2 className="trade__title">{trade.title}</h2>
                  <p className="prose prose--lg step__lead">{trade.lead}</p>
                  <p className="trade__note">{trade.note}</p>
                </div>

                <Card padding="md">
                  <p className="eyebrow eyebrow--sm eyebrow--subtle">
                    What the scan is looking for
                  </p>
                  <ul className="hazard-list">
                    {trade.hazards.map((hazard) => (
                      <li key={hazard} className="hazard-list__item">
                        {hazard}
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>
            </Reveal>
          ))}
        </ul>
      </Section>

      <CtaBand />
    </>
  );
}
